// src/pages/superadmin/ManageSchools.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Table,
  Button,
  Spinner,
  ButtonGroup,
  Modal,
  Form,
  InputGroup,
} from "react-bootstrap";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../services/firebase";
import {
  createSchoolWithAdmin, // create school + admin user + link
  updateSchool,
  removeSchool,
  updateSchoolCredentials, // updates admin.email (and optionally password if you wire CF)
} from "../../services/schools";
import SchoolFilterBar from "../../components/common/SchoolFilterBar";
import SubscriptionBadge from "../../components/common/SubscriptionBadge";
import SchoolFormModal from "../../components/schools/SchoolFormModal";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";

// ---------- helpers ----------
// Robust: Timestamp | {seconds} | number(ms) | ISO string | null/"" -> Date|null
const toDate = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === "function") {
    try {
      return v.toDate();
    } catch {
      /* fallthrough */
    }
  }
  if (typeof v === "object" && typeof v.seconds === "number") {
    const d = new Date(v.seconds * 1000);
    return isNaN(d) ? null : d;
  }
  if (typeof v === "number" || typeof v === "string") {
    const d = new Date(v);
    return isNaN(d) ? null : d;
  }
  return null;
};

const fmtDate = (d, f = "DD MMM YYYY") =>
  d && dayjs(d).isValid() ? dayjs(d).format(f) : "—";

const withinDays = (date, days) => {
  const d = date ? dayjs(date) : null;
  return !!(d && d.isValid() && d.isAfter(dayjs()) && d.diff(dayjs(), "day") <= days);
};

const isActive = (sub) => {
  const d = sub?.renewalDate ? dayjs(sub.renewalDate) : null;
  return sub?.status === "active" && d && d.isValid() && d.isAfter(dayjs());
};

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("");
};

const randomPass = (len = 12) =>
  Array.from(crypto.getRandomValues(new Uint32Array(len)))
    .map((n) => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%".charAt(n % 65))
    .join("");

// Normalize Firestore doc → row for table
const normalize = (doc) => {
  const data = doc.data();
  const sub = data.subscription || {};
  return {
    id: doc.id,
    // ✅ include code so edit modal can prefill it
    code: data.code || data.schoolCode || data.meta?.code || "",
    name: data.name || "—",
    strength: Number(data.strength || 0),
    plan: String(data.plan || "free").toLowerCase(),
    subscription: {
      status: sub.status || "expired",
      renewalDate: toDate(sub.renewalDate),
    },
    contact: {
      principal: data.principal || data.contact?.principal || "",
      email: data.email || data.contact?.email || "",
      phone: data.phone || data.contact?.phone || "",
      website: data.website || data.contact?.website || "",
    },
    address: {
      city: data.city || data.address?.city || "",
      state: data.state || data.address?.state || "",
      pincode: data.pincode || data.address?.pincode || "",
      addr1: data.addressLine1 || data.address?.addr1 || "",
      addr2: data.addressLine2 || data.address?.addr2 || "",
    },
    logoURL: data.logoURL || data.logoUrl || data.logo?.url || null,
    admin: {
      email: data.adminEmail || data.admin?.email || "",
      uid: data.adminUid || data.admin?.uid || "",
    },
    createdAt: toDate(data.createdAt),
  };
};

export default function ManageSchools() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [view, setView] = useState("all");
  const [plan, setPlan] = useState("all");
  const [search, setSearch] = useState("");
  const [expDays, setExpDays] = useState(14);

  // modals
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showCreds, setShowCreds] = useState(false);
  const [credsFor, setCredsFor] = useState(null);
  const [credEmail, setCredEmail] = useState("");
  const [credPw, setCredPw] = useState("");
  const [credSaving, setCredSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // fetch
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "schools"),
      (snap) => {
        setRows(snap.docs.map(normalize));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((s) => {
      if (view === "active" && !isActive(s.subscription)) return false;
      if (view === "expiring" && !withinDays(s.subscription?.renewalDate, expDays)) return false;
      if (plan !== "all" && (s.plan || "free") !== plan) return false;

      const q = search.trim().toLowerCase();
      if (q) {
        const hay = [s.name || "", s.id || "", s.contact?.principal || "", s.address?.city || "", s.plan || ""]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, view, plan, search, expDays]);

  const clearFilters = () => {
    setView("all");
    setPlan("all");
    setSearch("");
    setExpDays(14);
  };

  const openCreate = () => {
    setEditing(null);
    setShowModal(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setShowModal(true);
  };
  const openCreds = (row) => {
    setCredsFor(row);
    setCredEmail(row?.admin?.email || "");
    setCredPw("");
    setShowPw(false);
    setShowCreds(true);
  };

  // Create OR Update
  const save = async (payload) => {
    if (editing) {
      // ✅ Preserve code on update (and trim it)
      await updateSchool(editing.id, { ...payload, code: (payload.code || "").trim() });
      return Swal.fire({ icon: "success", title: "Updated", timer: 1200, showConfirmButton: false });
    }

    // CREATE new — also create admin user + show credentials once
    const schoolName = payload?.name || "School";
    const code = (payload?.code || "").trim().toLowerCase();
    const adminEmail = payload?.adminEmail?.trim() || (code ? `${code}@schools.learnsmart.app` : "");
    if (!adminEmail) {
      return Swal.fire({
        icon: "warning",
        title: "Admin email required",
        text: "Provide adminEmail or a school code to auto-generate.",
      });
    }
    const tempPassword = payload?.adminPassword?.trim() || randomPass(12);

    try {
      const { schoolId, admin } = await createSchoolWithAdmin({
        schoolPayload: payload, // services should normalize renewalDate -> Timestamp
        adminEmail,
        adminPassword: tempPassword,
        adminDisplayName: `${schoolName} Admin`,
      });

      await Swal.fire({
        icon: "success",
        title: "School Created",
        html: `
          <div style="text-align:left; line-height:1.55">
            <p><strong>School ID:</strong> ${schoolId}</p>
            <p><strong>Admin Email:</strong> ${admin.email}</p>
            <p><strong>Temp Password:</strong> <code>${tempPassword}</code></p>
          </div>
        `,
        confirmButtonText: "Copy & Close",
        preConfirm: () => {
          const text = `School ID: ${schoolId}
Admin Email: ${admin.email}
Temp Password: ${tempPassword}`;
          navigator.clipboard.writeText(text).catch(() => {});
        },
      });

      setShowModal(false);
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Failed to create", text: e?.message || "Please try again." });
    }
  };

  const saveCreds = async (e) => {
    e?.preventDefault?.();
    if (!credsFor?.id) return;

    if (credEmail && !/^\S+@\S+\.\S+$/.test(credEmail)) {
      return Swal.fire({ icon: "error", title: "Invalid email format" });
    }
    if (credPw && credPw.length < 6) {
      return Swal.fire({ icon: "error", title: "Password must be at least 6 characters" });
    }

    setCredSaving(true);
    try {
      await updateSchoolCredentials(credsFor.id, {
        adminEmail: credEmail || null,
        adminPassword: credPw || null, // only if you actually set via backend
      });
      Swal.fire({ icon: "success", title: "Credentials updated", timer: 1200, showConfirmButton: false });
      setShowCreds(false);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Failed to update credentials", text: err?.message || "Try again" });
    } finally {
      setCredSaving(false);
    }
  };

  const sendReset = async () => {
    const email = credEmail || credsFor?.admin?.email || "";
    if (!email) {
      return Swal.fire({ icon: "warning", title: "Email required to send reset" });
    }
    try {
      await sendPasswordResetEmail(getAuth(), email);
      Swal.fire({ icon: "success", title: "Reset email sent", text: `Sent to ${email}` });
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Failed to send reset email", text: e?.message || "Try again" });
    }
  };

  const removeRow = async (row) => {
    const res = await Swal.fire({
      title: "Delete school?",
      text: `This will remove "${row.name}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;
    await removeSchool(row.id);
    Swal.fire({ icon: "success", title: "Deleted", timer: 1000, showConfirmButton: false });
  };

  const exportCSV = () => {
    const data = filtered.map((s) => ({
      id: s.id,
      code: s.code || "",
      name: s.name,
      principal: s.contact?.principal || "",
      city: s.address?.city || "",
      strength: s.strength,
      plan: s.plan,
      status: s.subscription?.status || "",
      renewalDate: fmtDate(s.subscription?.renewalDate, "YYYY-MM-DD"),
      createdAt: fmtDate(s.createdAt, "YYYY-MM-DD"),
      logoURL: s.logoURL || "",
      adminEmail: s.admin?.email || "",
      adminUid: s.admin?.uid || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "schools");
    // Use .xlsx for a true workbook export
    XLSX.writeFile(wb, "schools_export.xlsx");
  };

  return (
    <Container fluid className="p-3">
      {/* Header actions */}
      <Row className="mb-3">
        <Col>
          <h5 className="mb-0">Manage Schools</h5>
          <small className="text-muted">Create, edit, filter, export, and manage credentials</small>
        </Col>
        <Col className="text-end">
          <ButtonGroup>
            <Button variant="outline-secondary" onClick={exportCSV}>
              <i className="bi bi-download me-1" /> Export
            </Button>
            <Button onClick={openCreate}>
              <i className="bi bi-plus-lg me-1" /> Add School
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="m-0">Schools</h6>
          <small className="text-muted">
            {loading ? "Loading…" : `${filtered.length} of ${rows.length} shown`}
          </small>
        </div>

        {/* Filters */}
        <SchoolFilterBar
          view={view}
          setView={setView}
          plan={plan}
          setPlan={setPlan}
          search={search}
          setSearch={setSearch}
          expDays={expDays}
          setExpDays={setExpDays}
          onClear={clearFilters}
        />

        <div className="table-responsive">
          <Table hover className="align-middle">
            <thead>
              <tr>
                <th className="d-none d-sm-table-cell">Logo</th>
                <th>School</th>
                <th className="d-none d-md-table-cell">Principal</th>
                <th className="d-none d-lg-table-cell">City</th>
                <th className="text-center">Strength</th>
                <th className="text-center d-none d-md-table-cell">Plan</th>
                <th className="text-center">Status</th>
                <th className="text-center d-none d-sm-table-cell">Renewal</th>
                <th className="text-center d-none d-lg-table-cell">Joined</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="text-center py-5">
                    <Spinner animation="border" size="sm" /> Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-4 text-muted">
                    No matching schools
                  </td>
                </tr>
              ) : (
                filtered
                  .sort((a, b) => (b.strength || 0) - (a.strength || 0))
                  .map((s) => (
                    <tr key={s.id}>
                      {/* Logo */}
                      <td className="d-none d-sm-table-cell" style={{ width: 64 }}>
                        {s.logoURL ? (
                          <img
                            src={s.logoURL}
                            alt={`${s.name} logo`}
                            style={{ height: 40, width: 40, objectFit: "cover", borderRadius: 8 }}
                          />
                        ) : (
                          <div
                            className="d-inline-flex justify-content-center align-items-center bg-light border"
                            style={{
                              height: 40,
                              width: 40,
                              borderRadius: 8,
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#666",
                            }}
                            title={s.name}
                          >
                            {getInitials(s.name)}
                          </div>
                        )}
                      </td>

                      {/* Name + ID */}
                      <td>
                        <div className="fw-semibold text-truncate" style={{ maxWidth: 240 }}>
                          {s.name}
                        </div>
                        <div className="text-muted small">
                          {s.id}
                          {s.code ? (
                            <>
                              {" "}| <code title="school code">{s.code}</code>
                            </>
                          ) : null}
                        </div>
                      </td>

                      {/* Principal */}
                      <td className="d-none d-md-table-cell">
                        {s.contact?.principal ? s.contact.principal : <span className="text-muted">—</span>}
                      </td>

                      {/* City */}
                      <td className="d-none d-lg-table-cell">
                        {s.address?.city ? s.address.city : <span className="text-muted">—</span>}
                      </td>

                      {/* Strength */}
                      <td className="text-center">{(s.strength || 0).toLocaleString()}</td>

                      {/* Plan */}
                      <td className="text-center text-capitalize d-none d-md-table-cell">{s.plan}</td>

                      {/* Status */}
                      <td className="text-center">
                        <SubscriptionBadge status={s.subscription?.status || "expired"} />
                        {withinDays(s.subscription?.renewalDate, 14) && (
                          <div className="small text-warning">expiring soon</div>
                        )}
                      </td>

                      {/* Renewal */}
                      <td className="text-center d-none d-sm-table-cell">{fmtDate(s.subscription?.renewalDate)}</td>

                      {/* Joined */}
                      <td className="text-center d-none d-lg-table-cell">{fmtDate(s.createdAt)}</td>

                      {/* Actions */}
                      <td className="text-end">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          className="me-2"
                          onClick={() => openEdit(s)}
                          title="Edit details"
                        >
                          <i className="bi bi-pencil" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          onClick={() => openCreds(s)}
                          title="Manage credentials"
                        >
                          <i className="bi bi-key" />
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => removeRow(s)} title="Delete">
                          <i className="bi bi-trash" />
                        </Button>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit modal */}
      <SchoolFormModal show={showModal} onHide={() => setShowModal(false)} initial={editing} onSave={save} />

      {/* Credentials modal */}
      <Modal show={showCreds} onHide={() => setShowCreds(false)} centered>
        <Form onSubmit={saveCreds}>
          <Modal.Header closeButton>
            <Modal.Title>Manage Credentials</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2">
              <div className="fw-semibold">{credsFor?.name || "—"}</div>
              <div className="text-muted small">{credsFor?.id}</div>
              {credsFor?.admin?.uid && (
                <div className="text-muted small mt-1">
                  <span className="me-2">UID:</span>
                  <code>{credsFor.admin.uid}</code>
                </div>
              )}
            </div>

            <Row className="g-3">
              <Col xs={12}>
                <Form.Label>Admin Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="admin@school.com"
                  value={credEmail}
                  onChange={(e) => setCredEmail(e.target.value)}
                />
                <div className="form-text">Leave blank to keep existing.</div>
              </Col>
              <Col xs={12}>
                <Form.Label>Admin Password</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPw ? "text" : "password"}
                    placeholder="Min 6 characters"
                    value={credPw}
                    onChange={(e) => setCredPw(e.target.value)}
                  />
                  <Button variant="outline-secondary" type="button" onClick={() => setShowPw((s) => !s)}>
                    <i className={`bi ${showPw ? "bi-eye-slash" : "bi-eye"}`} />
                  </Button>
                </InputGroup>
                <div className="form-text">
                  Prefer using “Send Password Reset Email” below instead of storing passwords.
                </div>
              </Col>
            </Row>

            <hr className="my-3" />
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="outline-primary"
                type="button"
                onClick={() => {
                  const text = `Admin Email: ${credEmail || credsFor?.admin?.email || ""}\nUID: ${
                    credsFor?.admin?.uid || ""
                  }`;
                  navigator.clipboard.writeText(text).then(() => {
                    Swal.fire({ icon: "success", title: "Copied", timer: 900, showConfirmButton: false });
                  });
                }}
              >
                Copy Email & UID
              </Button>
              <Button variant="outline-success" type="button" onClick={sendReset}>
                Send Password Reset Email
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowCreds(false)} disabled={credSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={credSaving}>
              {credSaving ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}
